import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaClock, FaCheckCircle } from 'react-icons/fa';

const Events = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const validate = () => {
    let tempErrors = {};
    if (!formData.name.trim()) tempErrors.name = "Name is required";
    if (!formData.email.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/)) tempErrors.email = "Invalid email format";
    if (!formData.subject) tempErrors.subject = "Subject is required";
    if (formData.message.length < 10) tempErrors.message = "Message must be at least 10 characters";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      setIsSubmitting(true);
      setTimeout(() => {
        setIsSubmitting(false);
        setSuccessMsg("Thank you! Your message has been sent successfully.");
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSuccessMsg(''), 5000);
      }, 2000);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  return (
    <div className="min-h-screen bg-white font-sans overflow-x-hidden">
      {/* 1. HERO SECTION */}
      <section 
        className="relative h-[45vh] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('/Garba(2).jpeg')` }}
      >
        <div className="text-center text-white">
          <motion.h1 
            initial={{ y: 20, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            className="text-5xl md:text-6xl font-serif font-black uppercase tracking-tight"
          >
            Events
          </motion.h1>
          <div className="h-1.5 w-20 bg-[#f9b50b] mx-auto mt-4"></div>
          <p className="mt-4 uppercase tracking-[0.3em] text-xs font-bold">
            <Link to="/" className="hover:text-[#f9b50b] text-gray-300 transition-colors">Home</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-white">Our Community Events</span>
          </p>
        </div>
      </section>


      <section className="py-24 bg-white px-6 max-w-7xl mx-auto">
              <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
                
                {/* LEFT SIDE: Content & Detailed Boxes (Col-span 7) */}
                <div className="lg:col-span-7 space-y-10">
                  <div>
                    <h2 className="text-5xl font-serif font-bold uppercase text-[#221f1f]">EVENTS FOR A <span className="text-[#f9b50b]">DIVERSE COMMUNITY</span></h2>
                    <div className="h-1 w-20 bg-[#f9b50b] mt-4"></div>
                    <p className="text-gray-600 text-lg mt-6 leading-relaxed">
                      The Illinois Interfaith Organization hosts inclusive events that bring people of diverse faiths, cultures, and backgrounds together to build understanding, trust, and lasting relationships. Each event is designed to encourage meaningful interaction, shared learning, and collective action.
                    </p>
                  </div>
      
                  {/* Event Description Boxes */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                      { title: "Interfaith Prayer Gatherings", desc: "These gatherings provide a peaceful and welcoming space for individuals from different faith traditions to come together in reflection and prayer. They honor the spiritual diversity of our community while promoting unity, compassion, and mutual respect." },
                      { title: "Cultural Festivals", desc: "Our cultural festivals celebrate the rich traditions, food, music, and art of diverse communities. These events create opportunities for people to learn about one another’s heritage while strengthening appreciation for cultural diversity." },
                      { title: "Community Dinners", desc: "Community dinners bring people together around a shared meal, fostering connection and open conversation in a warm and inclusive environment. These gatherings help build friendships, break down barriers, and encourage dialogue across differences." },
                      { title: "Guest Speakers Events", desc: "We host speakers from various faiths, cultures, and professional backgrounds to share their experiences and perspectives. These events promote learning, broaden understanding, and inspire thoughtful conversations on faith, culture, and social issues." },
                      { title: "Panel Discussions", desc: "Panel discussions bring together diverse voices to explore important topics such as interfaith cooperation, human rights, and social justice. These forums encourage respectful dialogue, critical thinking, and collaborative problem-solving." },
                      { title: "Workshops and Training Sessions", desc: "Our workshops and trainings focus on building skills such as leadership, communication, conflict resolution, and community engagement. Participants gain practical tools while strengthening relationships across cultural and faith boundaries." },
                      { title: "Volunteer Service Projects", desc: "Volunteer initiatives unite individuals from different backgrounds to serve the community and address shared needs. By working side by side, participants build trust, strengthen relationships, and create a lasting impact through collective action." }
      
                    ].map((item, index) => (
                      <div key={index} className="p-6 border-l-4 border-[#f9b50b] bg-gray-50 hover:bg-white hover:shadow-md transition-all">
                        <h4 className="font-bold text-[#221f1f] uppercase text-sm mb-2">{item.title}</h4>
                        <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                      </div>
                    ))}
                  </div>

                  {/* CTA below collage */}
            <div className="text-center lg:text-left">
              <Link 
                to="/gallery" 
                className="inline-block bg-[#221f1f] text-white px-12 py-4 font-black uppercase text-xs tracking-[0.2em] hover:bg-[#f9b50b] hover:text-[#221f1f] transition-all shadow-2xl"
              >
                View Gallery
              </Link>
            </div>
                </div>
      
                {/* RIGHT SIDE: Extended Image Collage (Col-span 5) */}
                <div className="lg:col-span-5 grid grid-cols-2 gap-4 h-full">
                  <div className="space-y-4">
                    <img src="/Garba.jpeg" alt="Event 1" className="rounded-lg h-72 w-full object-cover shadow-md" />
                    <img src="/Iftaar Dinner.jpeg" alt="Event 2" className="rounded-lg h-56 w-full object-cover shadow-md" />
                    <img src="/Community Bash.jpeg" alt="Event 3" className="rounded-lg h-64 w-full object-cover shadow-md" />
                    <img src="/Community Bash (4).jpeg" alt="Event 3" className="rounded-lg h-64 w-full object-cover shadow-md" />
                  </div>
                  <div className="pt-12 space-y-4">
                    <img src="/Adam & Eve Day.jpeg" alt="Event 4" className="rounded-lg h-56 w-full object-cover shadow-md" />
                    <img src="/Eid Namaz.jpeg" alt="Event 5" className="rounded-lg h-72 w-full object-cover shadow-md" />
                    <img src="/Garba(1).jpeg" alt="Event 6" className="rounded-lg h-60 w-full object-cover shadow-md" />
                    <img src="/Eid Milaad Un Nabi (2).jpeg" alt="Event 3" className="rounded-lg h-64 w-full object-cover shadow-md" />
                  </div>
                </div>
              </div>
            </section>

      {/* 3.  CONTACT  */}
            <section className="pb-24 px-6 max-w-7xl mx-auto mt-20">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
                
                {/* LEFT COLUMN: CONTACT INFO */}
                <div className="lg:col-span-1 space-y-12">
                  <div className="flex gap-5">
                    <FaMapMarkerAlt className="text-[#f9b50b] text-2xl mt-1" />
                    <div >
                      <h4 className="font-serif font-bold text-xl uppercase mb-3">Office Address</h4>
                      <a 
                        href="https://www.google.com/maps/search/?api=1&query=1645+W+Irving+Park+Rd+Suite+100+Hanover+Park+IL+60133" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-gray-500 text-sm not-italic leading-relaxed hover:text-[#f9b50b] transition-colors"
                      >
                        1645 W Irving Park Rd, Suite # 100 <br />
                        Hanover Park, IL, United States, Illinois 60133
                      </a>
                    </div>
                  </div>
      
                  <div className="flex gap-5">
                    <FaPhoneAlt className="text-[#f9b50b] text-2xl mt-1" />
                    <div>
                      <h4 className="font-serif font-bold text-xl uppercase mb-3">Phone</h4>
                      <a href="tel:18477377065" className="text-gray-500 text-sm hover:text-[#f9b50b] transition-colors">+1 (847) 737-7065</a>
                    </div>
                  </div>
      
                  <div className="flex gap-5">
                    <FaEnvelope className="text-[#f9b50b] text-2xl mt-1" />
                    <div>
                      <h4 className="font-serif font-bold text-xl uppercase mb-3">Email</h4>
                      <a href="mailto:info@ilinterfaith.org" className="text-gray-500 text-sm hover:text-[#f9b50b] transition-colors">info@ilinterfaith.org</a>
                    </div>
                  </div>
                </div>
      
                {/* RIGHT COLUMN: CONTACT FORM */}
                <div className="lg:col-span-2">
                  <h3 className="text-2xl font-serif font-bold uppercase mb-8">Send Us Email</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <input 
                          type="text" name="name" value={formData.name} onChange={handleChange}
                          placeholder="Enter your full name" 
                          className={`w-full p-4 bg-gray-50 border ${errors.name ? 'border-red-500' : 'border-gray-100'} focus:outline-none focus:bg-white focus:border-[#f9b50b] transition-all text-sm`}
                        />
                        {errors.name && <p className="text-red-500 text-[10px] uppercase font-bold pl-1">{errors.name}</p>}
                      </div>
                      <div className="space-y-1">
                        <input 
                          type="email" name="email" value={formData.email} onChange={handleChange}
                          placeholder="Enter your email" 
                          className={`w-full p-4 bg-gray-50 border ${errors.email ? 'border-red-500' : 'border-gray-100'} focus:outline-none focus:bg-white focus:border-[#f9b50b] transition-all text-sm`}
                        />
                        {errors.email && <p className="text-red-500 text-[10px] uppercase font-bold pl-1">{errors.email}</p>}
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <input 
                        type="text" name="subject" value={formData.subject} onChange={handleChange}
                        placeholder="Subject" 
                        className={`w-full p-4 bg-gray-50 border ${errors.subject ? 'border-red-500' : 'border-gray-100'} focus:outline-none focus:bg-white focus:border-[#f9b50b] transition-all text-sm`}
                      />
                      {errors.subject && <p className="text-red-500 text-[10px] uppercase font-bold pl-1">{errors.subject}</p>}
                    </div>
      
                    <div className="space-y-1">
                      <textarea 
                        name="message" rows="8" value={formData.message} onChange={handleChange}
                        placeholder="Write your message" 
                        className={`w-full p-4 bg-gray-50 border ${errors.message ? 'border-red-500' : 'border-gray-100'} focus:outline-none focus:bg-white focus:border-[#f9b50b] transition-all text-sm`}
                      ></textarea>
                      {errors.message && <p className="text-red-500 text-[10px] uppercase font-bold pl-1">{errors.message}</p>}
                    </div>
      
                    <button 
                      disabled={isSubmitting}
                      className="bg-[#221f1f] text-white font-bold uppercase tracking-widest px-10 py-4 hover:bg-[#221f1f] transition-all disabled:bg-gray-400 mt-4 text-xs shadow-lg"
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </button>
      
                    {successMsg && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        className="mt-6 p-4 bg-green-50 border border-green-200"
                      >
                        <p className="text-green-700 font-bold text-center text-sm uppercase tracking-tight">
                          {successMsg}
                        </p>
                      </motion.div>
                    )}
                  </form>
                </div>
              </div>
            </section>
    </div>
  );
};

export default Events;